import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader } from '@hopskipdrive/react-components';

import {
  analyticsCompleteRegistration,
  analyticsOrganizerSignup,
  analyticsPage,
} from '../../../api/services/Analytics';
import * as AuthService from '../../../api/services/Auth';
import { useFlags } from '../../../contexts/LDContext';
import { useToastStore } from '../../../shared/stores/toastStore';
import { LoginTemplate } from '../../features/Login/LoginTemplate';
import { NewUserForm } from '../../features/NewUserForm';

const userState = {
  firstName: '',
  lastName: '',
  mobilePhoneNumber: '',
  password: '',
  passwordConfirmation: '',
  roles: [],
};

const SignUp = () => {
  const [loading, setLoading] = useState(true);
  const [disableSubmitButton, setDisableSubmitButton] = useState(false);
  const [user, setUser] = useState<UserFormData>(userState);
  const [ssoRequired, setSsoRequired] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const confirmationToken = window.sessionStorage.getItem('confirmation_token');
  const { addToast } = useToastStore();
  const { enableDailyQueueAssignedLocations } = useFlags();

  useEffect(() => {
    if (queryParams.has('confirmation_token')) {
      const confirmationTokenParam = queryParams.get('confirmation_token');

      window.sessionStorage.setItem(
        'confirmation_token',
        confirmationTokenParam !== null ? confirmationTokenParam : ''
      );
      queryParams.delete('confirmation_token');
      navigate(
        {
          search: queryParams.toString(),
        },
        { replace: true }
      );
    } else if (!confirmationToken) {
      setLoading(false);
    }
    analyticsPage('Sign Up');
  }, []);

  useEffect(() => {
    if (confirmationToken) {
      welcome(confirmationToken);
    }
  }, [confirmationToken]);

  const setLocalStorageBasedOnState = (state: 'active' | 'pending_approval') => {
    if (state === 'pending_approval') {
      window.localStorage.setItem('pendingApprovalModal', 'true');
    }
  };

  const welcome = async (existingConfirmationToken: string | null) => {
    const response = await AuthService.welcome(existingConfirmationToken || '');

    if (response?.type === 'success') {
      const { ssoRequired: ssoRequiredFlag, ...userData } = response.data.user;

      setUser(userData as UserFormData);
      setSsoRequired(ssoRequiredFlag ?? false);
    } else {
      window.sessionStorage.setItem(
        'toastMessages',
        JSON.stringify({ severity: 'error', messages: response.error.errors })
      );

      navigate('/login');
    }
    setLoading(false);
  };

  const signUp = async (data, e) => {
    e.preventDefault();
    setDisableSubmitButton(true);
    const { location: loc, ...rest } = data;

    const response = await AuthService.signUp({
      confirmation_token: confirmationToken,
      ...(data.roles.includes('onsite_staff') &&
        !enableDailyQueueAssignedLocations &&
        loc && { defaultAccountLocationId: loc.id }),
      ...rest,
    });

    if (response?.type === 'success') {
      analyticsCompleteRegistration();
      data.roles.includes('onsite_staff') && analyticsOrganizerSignup(data.jobTitle, loc?.address);
      window.sessionStorage.removeItem('confirmation_token');
      setLocalStorageBasedOnState(response?.data?.state);
      window.sessionStorage.setItem(
        'toastMessages',
        JSON.stringify({ severity: 'info', messages: [response.data.message] })
      );
      navigate('/login');
    } else {
      response.error.errors.forEach((error) => addToast({ severity: 'error', message: error }));
    }
    setDisableSubmitButton(false);
  };

  return (
    <LoginTemplate banner={false}>
      {!loading ? (
        <NewUserForm
          user={user}
          handleNewUser={signUp}
          confirmationToken={confirmationToken}
          disableSubmitButton={disableSubmitButton}
          ssoRequired={ssoRequired}
        />
      ) : (
        <Loader />
      )}
    </LoginTemplate>
  );
};

export default SignUp;
